import React from 'react';
import { RefreshControlProps, ScrollView, StyleProp, View, ViewStyle } from 'react-native';
import { CONTENT_MAX_WIDTH } from '../../assets/theme';
import { makeStyles } from '../../theme/ThemeContext';
import { useLayout } from '../../theme/useLayout';

type ScreenProps = {
  children: React.ReactNode;
  /** Wrap the content in a ScrollView (default). Use false for screens whose root is a FlatList. */
  scroll?: boolean;
  refreshControl?: React.ReactElement<RefreshControlProps>;
  /** Style applied to the centered content column. */
  contentStyle?: StyleProp<ViewStyle>;
  /** Let the content use the full width instead of the centered max-width column. */
  fullWidth?: boolean;
};

/**
 * Page canvas: themed background + centered max-width column with responsive gutters.
 */
export default function Screen({ children, scroll = true, refreshControl, contentStyle, fullWidth }: ScreenProps) {
  const styles = useStyles();
  const { gutter, isDesktop } = useLayout();
  const column: StyleProp<ViewStyle> = [
    styles.column,
    { paddingHorizontal: gutter, paddingTop: isDesktop ? 32 : 16 },
    fullWidth ? null : styles.maxWidth,
    contentStyle,
  ];

  if (!scroll) {
    return (
      <View style={styles.root}>
        <View style={[column, styles.fill]}>{children}</View>
      </View>
    );
  }

  return (
    <ScrollView
      style={styles.root}
      contentContainerStyle={styles.scrollContent}
      refreshControl={refreshControl}
      keyboardShouldPersistTaps="handled"
    >
      <View style={column}>{children}</View>
    </ScrollView>
  );
}

const useStyles = makeStyles((c) => ({
  root: { flex: 1, backgroundColor: c.background },
  scrollContent: { flexGrow: 1, paddingBottom: 48 },
  column: { width: '100%', alignSelf: 'center' },
  maxWidth: { maxWidth: CONTENT_MAX_WIDTH },
  fill: { flex: 1 },
}));
